import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

export default function LiveRoomPanel({ roomId, audioOnly = false, tokenEndpoint, tokenBody, guest = false, onSession }) {
  const session = useRef(null);
  const media = useRef(null);
  const version = useRef(0);
  const sessionChanged = useRef(onSession); sessionChanged.current = onSession;
  const [state, setState] = useState("disconnected");
  const [error, setError] = useState("");
  const [publish, setPublish] = useState(false);
  const [devices, setDevices] = useState({ microphone: false, camera: false, screen: false });
  const [busy, setBusy] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const disconnect = () => {
    version.current++;
    const current = session.current;
    session.current = null;
    sessionChanged.current?.(null);
    if (current) { current.removeAllListeners(); void current.disconnect(); }
    media.current?.replaceChildren();
  };
  useEffect(() => {
    setState("disconnected"); setError(""); setDevices({ microphone: false, camera: false, screen: false });
    return disconnect;
  }, [roomId]);
  const leave = () => { disconnect(); setState("disconnected"); setPublish(false); setDevices({ microphone: false, camera: false, screen: false }); };
  const join = async () => {
    if (state !== "disconnected") return;
    const request = ++version.current;
    setState("connecting"); setError("");
    try {
      const { data } = await api.post(tokenEndpoint || `/rooms/${roomId}/token`, tokenBody);
      const { Room, RoomEvent, Track } = await import("livekit-client");
      if (request !== version.current) return;
      const current = new Room({ adaptiveStream: true, dynacast: true });
      session.current = current;
      const attached = new Set();
      const attach = (track, local = false) => {
        if (!track || attached.has(track) || (local && track.kind !== Track.Kind.Video)) return;
        attached.add(track);
        const element = track.attach();
        element.autoplay = true; element.playsInline = true; element.muted = local;
        element.className = "max-h-64 w-full rounded bg-black object-contain";
        element.setAttribute("aria-label", local ? "Votre vidéo" : "Média du participant");
        media.current?.appendChild(element);
      };
      const detach = (track) => { if (track) { attached.delete(track); track.detach().forEach((el) => el.remove()); } };
      const sync = () => {
        if (request !== version.current) return;
        setDevices({ microphone: current.localParticipant.isMicrophoneEnabled, camera: current.localParticipant.isCameraEnabled, screen: current.localParticipant.isScreenShareEnabled });
      };
      current.on(RoomEvent.TrackSubscribed, (track) => attach(track));
      current.on(RoomEvent.TrackUnsubscribed, detach);
      current.on(RoomEvent.LocalTrackPublished, (publication) => { attach(publication.track, true); sync(); });
      current.on(RoomEvent.LocalTrackUnpublished, (publication) => { detach(publication.track); sync(); });
      current.on(RoomEvent.TrackMuted, sync); current.on(RoomEvent.TrackUnmuted, sync);
      current.on(RoomEvent.ConnectionStateChanged, (next) => { if (request === version.current) setState(next); });
      current.on(RoomEvent.Disconnected, () => {
        if (request !== version.current) return;
        attached.forEach(detach); setState("disconnected"); setPublish(false); sync();
        session.current = null;
        sessionChanged.current?.(null);
      });
      current.on(RoomEvent.MediaDevicesError, (err) => { if (request === version.current) setError(apiError(err, "Impossible d’accéder au périphérique.")); });
      setPublish(data.role !== "viewer");
      await current.connect(data.ws_url, data.token);
      if (request !== version.current) { await current.disconnect(); return; }
      setState("connected");
      sessionChanged.current?.(current);
      try { await current.startAudio(); setAudioBlocked(false); }
      catch (_) { setAudioBlocked(true); }
    } catch (err) {
      if (request !== version.current) return;
      disconnect(); setState("disconnected"); setError(apiError(err, "Connexion à la salle impossible."));
    }
  };
  const toggle = async (kind) => {
    if (!session.current || !publish || busy || state !== "connected") return;
    const current = session.current;
    setBusy(true); setError("");
    try {
      const participant = current.localParticipant;
      if (kind === "microphone") await participant.setMicrophoneEnabled(!participant.isMicrophoneEnabled);
      if (kind === "camera") await participant.setCameraEnabled(!participant.isCameraEnabled);
      if (kind === "screen") await participant.setScreenShareEnabled(!participant.isScreenShareEnabled);
      if (session.current === current) setDevices({ microphone: participant.isMicrophoneEnabled, camera: participant.isCameraEnabled, screen: participant.isScreenShareEnabled });
    } catch (err) { if (session.current === current) setError(apiError(err, "Action média impossible. Vérifiez les autorisations du navigateur.")); }
    finally { setBusy(false); }
  };
  return <section className="mb-6 space-y-4 rounded-md border border-zinc-800 p-5" aria-label="Connexion audio et vidéo">
    <h2 className="font-medium">Rejoindre la salle</h2>
    <p className="text-xs text-zinc-400">{guest ? "Rejoignez depuis votre navigateur. Vous êtes spectateur par défaut." : "Connectez-vous avec votre compte. Le micro et la caméra restent désactivés jusqu’à votre action."}</p>
    <p role="status" className="text-sm">{({ disconnected: "Déconnecté", connecting: "Connexion…", connected: "Connecté", reconnecting: "Reconnexion…", signalReconnecting: "Reconnexion…" })[state] || state}</p>
    {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
    <div className="flex flex-wrap gap-2">
      {state === "disconnected" ? <Button onClick={join}>Rejoindre</Button> : <Button variant="outline" onClick={leave}>Quitter</Button>}
      {state === "connected" && publish && <>
        <Button variant="outline" disabled={busy} onClick={() => toggle("microphone")}>{devices.microphone ? "Couper le micro" : "Activer le micro"}</Button>
        {!audioOnly && <Button variant="outline" disabled={busy} onClick={() => toggle("camera")}>{devices.camera ? "Couper la caméra" : "Activer la caméra"}</Button>}
        <Button variant="outline" disabled={busy} onClick={() => toggle("screen")}>{devices.screen ? "Arrêter le partage" : "Partager l’écran"}</Button>
      </>}
      {audioBlocked && state === "connected" && <Button variant="outline" onClick={async () => { try { await session.current?.startAudio(); setAudioBlocked(false); } catch (err) { setError(apiError(err)); } }}>Activer le son</Button>}
    </div>
    {state === "connected" && !publish && <p className="text-xs text-zinc-400">Votre rôle permet uniquement de regarder et d’écouter.</p>}
    <div ref={media} className="grid gap-3 sm:grid-cols-2" />
  </section>;
}
